import React from 'react';
import { useTranslation } from 'react-i18next';
import { numberFormat } from './../../utils/functions';
import './PaymentTypeChoices.css';

const TITLE_KEY = { full: 'payment_method_full', deposit: 'payment_method_deposit', payonstay: 'payment_method_pay_on_stay' };

/**
 * การ์ดเลือกวิธีชำระเงิน -- แทนปุ่มกลมเล็กๆ แบบเดิม
 *
 * ปัญหาเดิม: ค่าเริ่มต้นเลือก "ชำระเต็มจำนวน" ไว้ให้ และตัวเลือกไม่บอกยอด ลูกค้าที่ตั้งใจมัดจำ
 * เห็นคอลัมน์ "มัดจำ" ในตารางแล้วโอนแค่ยอดนั้น ทั้งที่รายการยังเป็นจ่ายเต็ม
 * ตอนนี้แต่ละตัวเลือกบอกยอดที่ต้องจ่ายตอนนี้ แยกเงินประกันให้เห็น และบอกยอดคงเหลือ
 * และไม่เลือกให้ล่วงหน้า -- ลูกค้าต้องเลือกเอง
 *
 * choices: [{ type, breakdown (จาก paymentBreakdown), subtitle? }]
 */
const PaymentTypeChoices = React.forwardRef(({ choices, value, onChange, invalid = false, note = null }, ref) => {
  const { t } = useTranslation();
  const baht = (v) => `฿${numberFormat(v, true)}`;

  return (
    <div ref={ref} className={`pay-choices${invalid ? ' pay-choices--invalid' : ''}`}>
      <div className="pay-choices__title">
        {t('pay_type_choose_title')}
        {note && <small className="text-muted d-block fw-normal">{note}</small>}
      </div>
      {invalid && <div className="pay-choices__error">{t('please_select_payment_type')}</div>}
      <div className="pay-choices__list" role="radiogroup">
        {choices.map(({ type, breakdown: b, subtitle }) => {
          const selected = value === type;
          return (
            <button
              type="button"
              key={type}
              role="radio"
              aria-checked={selected}
              className={`pay-choice${selected ? ' is-selected' : ''}`}
              onClick={() => onChange(type)}
            >
              <span className="pay-choice__radio" aria-hidden="true" />
              <span className="pay-choice__body">
                <span className="pay-choice__head">
                  <span className="pay-choice__name">{t(TITLE_KEY[type])}{subtitle ? <small> {subtitle}</small> : null}</span>
                  <span className="pay-choice__now">
                    <small>{t('pay_now')}</small>
                    <strong>{baht(b.payNow)}</strong>
                  </span>
                </span>
                <span className="pay-choice__lines">
                  {type === 'full' && b.roomPart > 0 && <span>{t('pay_line_room_full')} {baht(b.roomPart + b.addonPart)}</span>}
                  {type === 'deposit' && <span>{t('pay_line_deposit')} {baht(b.roomPart)}</span>}
                  {type === 'deposit' && b.addonPart > 0 && <span>{t('pay_line_addons')} {baht(b.addonPart)}</span>}
                  {type === 'payonstay' && b.addonPart > 0 && <span>{t('pay_line_addons')} {baht(b.addonPart)}</span>}
                  {b.securityNow > 0 && <span>{t('pay_line_security')} {baht(b.securityNow)} <em>({t('pay_line_security_refund')})</em></span>}
                  {b.securityLater > 0 && <span className="text-muted">{t('pay_line_security_later', { amount: baht(b.securityLater) })}</span>}
                </span>
                <span className={`pay-choice__remain${b.remaining > 0 ? '' : ' is-zero'}`}>
                  {b.remaining > 0 ? t('pay_remaining_on_stay', { amount: baht(b.remaining) }) : t('pay_no_remaining')}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default PaymentTypeChoices;
